import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { objectOmit } from '@/_shared/helpers';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_JOBID_RECORDS_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import { API_GET_EMPLOYEES_IMPORT, KEY_GET_EMPLOYEES_IMPORT } from '../constants';
import { MOCK_RESPONSE_GET_EMPLOYEES_IMPORT } from '../mocks';
import { ImportedEmployer } from '../types';

type GenericGetEmployeesImport = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_JOBID_RECORDS_PATH_KEY
>;

type EmployeesImportQueryParams = GenericGetEmployeesImport['request']['query'];

type EmployeesImportPathParams = Omit<
  GenericGetEmployeesImport['request']['path'],
  'organizationId'
>;

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetEmployeesImport, 'successSaga' | 'errorSaga'>;
};

type RequestEmployeesImport = EmployeesImportPathParams & EmployeesImportQueryParams;

type ResponseEmployeesImport = GenericGetEmployeesImport['response']['body'];

export type CallParamsGetEmployeesImport = RequestEmployeesImport & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetEmployeesImport = ResponseEmployeesImport;

type CallErrorGetEmployeesImport = GenericApiError;

type CallSelectorsGetEmployeesImport = Omit<ResponseEmployeesImport, 'data'> & {
  data: ImportedEmployer[];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getEmployeesImportCall = createGeneralCall<
  CallParamsGetEmployeesImport,
  CallResponseGetEmployeesImport,
  CallErrorGetEmployeesImport,
  CallSelectorsGetEmployeesImport
>({
  id: KEY_GET_EMPLOYEES_IMPORT,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      const PATH_PARAMS_KEYS_TO_OMIT = {
        jobId: true,
      } as const satisfies Record<keyof EmployeesImportPathParams, true>;

      const queryParams = objectOmit(payload, [
        'successSaga',
        'errorSaga',
        ...(Object.keys(PATH_PARAMS_KEYS_TO_OMIT) as (keyof EmployeesImportPathParams)[]),
      ]);

      return API_GET_EMPLOYEES_IMPORT(org, payload.jobId, queryParams);
    },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_EMPLOYEES_IMPORT : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
});
